import {
  ChevronDown,
  ChevronRight,
  FolderPlus,
  Layers3,
  Search,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import axios from "axios";

const catalogApi = `${process.env.REACT_APP_BACKEND_URL}/api/catalog`;

const explorerTypes = [
  "catalog_category",
  "card_finish",
  "card_type",
  "classification",
  "game",
  "game_variant",
  "language",
  "promotional_set",
  "publisher",
  "rarity",
  "region",
  "era",
  "set",
  "set_symbol",
  "subset",
];

const typeLabels = {
  catalog_category: "Categories",
  card_finish: "Card finishes",
  card_type: "Card types",
  classification: "Classifications",
  era: "Eras & series",
  game: "Games",
  game_variant: "Game variants",
  language: "Languages",
  promotional_set: "Promotional sets",
  publisher: "Publishers",
  rarity: "Rarities",
  region: "Regions",
  set: "Sets",
  set_symbol: "Set symbols",
  subset: "Subsets",
};

const parentIds = (record) =>
  Object.values(record.parents || {}).filter(Boolean);

function ExplorerNode({ activeId, childMap, onSelect, record }) {
  const [isOpen, setIsOpen] = useState(false);
  const children = childMap[record.record_id] || [];
  const label = record.display_name || record.name;

  return (
    <li className="catalog-explorer-node">
      <div className={activeId === record.record_id ? "is-selected" : ""}>
        <button
          aria-label={isOpen ? `Collapse ${label}` : `Expand ${label}`}
          className="catalog-tree-toggle"
          disabled={!children.length}
          onClick={() => setIsOpen((current) => !current)}
          type="button"
          data-testid={`catalog-tree-toggle-${record.record_id}`}
        >
          {children.length ? (
            isOpen ? (
              <ChevronDown size={14} />
            ) : (
              <ChevronRight size={14} />
            )
          ) : (
            <span />
          )}
        </button>
        <button
          className="catalog-tree-record"
          onClick={() => onSelect(record)}
          type="button"
          data-testid={`catalog-tree-record-${record.record_id}`}
        >
          <span>{label}</span>
          <small>{record.status}</small>
        </button>
      </div>
      {isOpen && children.length > 0 && (
        <ul>
          {children.map((child) => (
            <li
              className={
                activeId === child.record_id
                  ? "catalog-explorer-child is-selected"
                  : "catalog-explorer-child"
              }
              key={child.record_id}
            >
              <button
                className="catalog-tree-record"
                onClick={() => onSelect(child)}
                type="button"
                data-testid={`catalog-tree-record-${child.record_id}`}
              >
                <span>{child.display_name || child.name}</span>
                <small>{child.status}</small>
              </button>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

export default function CatalogExplorer({
  activeRecord,
  onCreate,
  onSelect,
  onTypeSelect,
  refreshKey,
}) {
  const [records, setRecords] = useState([]);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [treeSearch, setTreeSearch] = useState("");

  useEffect(() => {
    let mounted = true;
    const loadExplorer = async () => {
      setLoading(true);
      try {
        const responses = await Promise.all(
          explorerTypes.map((recordType) =>
            axios.get(`${catalogApi}/records`, {
              params: {
                limit: 100,
                record_type: recordType,
                sort: "name_asc",
              },
              withCredentials: true,
            }),
          ),
        );
        if (mounted) {
          setRecords(
            responses.flatMap(
              (response) => response.data.items || response.data.records || [],
            ),
          );
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };
    loadExplorer();
    return () => {
      mounted = false;
    };
  }, [refreshKey]);

  const { childMap, rootRecords, typeCounts } = useMemo(() => {
    const ids = new Set(records.map((record) => record.record_id));
    const children = {};
    const roots = [];
    const counts = {};
    records.forEach((record) => {
      counts[record.record_type] = (counts[record.record_type] || 0) + 1;
      const parentId = parentIds(record).find((id) => ids.has(id));
      if (parentId) {
        children[parentId] = [...(children[parentId] || []), record];
      } else {
        roots.push(record);
      }
    });
    return { childMap: children, rootRecords: roots, typeCounts: counts };
  }, [records]);
  const filteredRoots = rootRecords.filter((record) => {
    const query = treeSearch.trim().toLowerCase();
    return (
      !query ||
      `${record.name} ${record.display_name}`.toLowerCase().includes(query)
    );
  });

  if (isCollapsed) {
    return (
      <aside
        className="catalog-explorer is-collapsed"
        data-testid="catalog-explorer"
      >
        <button
          aria-label="Expand catalog explorer"
          className="catalog-explorer-collapse"
          onClick={() => setIsCollapsed(false)}
          type="button"
          data-testid="catalog-explorer-expand-button"
        >
          <Layers3 size={18} />
        </button>
      </aside>
    );
  }

  return (
    <aside className="catalog-explorer" data-testid="catalog-explorer">
      <div className="catalog-explorer-heading">
        <div>
          <span>Catalog explorer</span>
          <small>Relationships and records</small>
        </div>
        <button
          aria-label="Collapse catalog explorer"
          className="catalog-explorer-collapse"
          onClick={() => setIsCollapsed(true)}
          type="button"
          data-testid="catalog-explorer-collapse-button"
        >
          <ChevronLeftIcon />
        </button>
      </div>
      <div className="catalog-explorer-types">
        {Object.entries(typeLabels).map(([type, label]) => (
          <button
            key={type}
            onClick={() => onTypeSelect(type)}
            type="button"
            data-testid={`catalog-explorer-type-${type}`}
          >
            <span>{label}</span>
            <b>{typeCounts[type] || 0}</b>
          </button>
        ))}
      </div>
      <div className="catalog-explorer-tree">
        <div className="catalog-explorer-tree-heading">
          <span>Hierarchy</span>
          <Search size={13} />
        </div>
        <input
          className="catalog-explorer-search"
          onChange={(event) => setTreeSearch(event.target.value)}
          placeholder="Find in catalog"
          value={treeSearch}
          data-testid="catalog-explorer-search-input"
        />
        {loading ? (
          <p
            className="catalog-explorer-loading"
            data-testid="catalog-explorer-loading"
          >
            Reading the catalog…
          </p>
        ) : filteredRoots.length ? (
          <ul data-testid="catalog-explorer-tree-list">
            {filteredRoots.map((record) => (
              <ExplorerNode
                activeId={activeRecord?.record_id}
                childMap={childMap}
                key={record.record_id}
                onSelect={onSelect}
                record={record}
              />
            ))}
          </ul>
        ) : (
          <div
            className="catalog-explorer-empty"
            data-testid="catalog-explorer-empty-state"
          >
            <Layers3 size={18} />
            <p>No manual catalog records yet.</p>
            <button
              onClick={() => onCreate("game")}
              type="button"
              data-testid="catalog-explorer-create-first-game"
            >
              <FolderPlus size={14} /> Create your first Game
            </button>
          </div>
        )}
      </div>
    </aside>
  );
}

function ChevronLeftIcon() {
  return <ChevronRight size={16} className="catalog-explorer-collapse-icon" />;
}
